import { useEffect } from "react";
import { Nav } from "./components/Nav.js";
import { Hero } from "./components/Hero.js";
import { LogoMarquee } from "./components/LogoMarquee.js";
import { Pillars } from "./components/Pillars.js";
import { AgentExamples } from "./components/AgentExamples.js";
import { HowItWorks } from "./components/HowItWorks.js";
import { FAQ } from "./components/FAQ.js";
import { FinalCTA } from "./components/FinalCTA.js";
import { Footer } from "./components/Footer.js";

/**
 * Skill-first landing. The pitch is one thing — install the Axon skill
 * and any AI agent can trade, non-custodially — and the page is a single
 * scannable narrative arc that proves it:
 *
 *   Hero          install one skill → your agent can trade  (the promise)
 *   LogoMarquee   the agents + venues it works across        (universality)
 *   Pillars       why it's safe — four guarantees            (the "why")
 *   AgentExamples what your agent can actually do            (the "what")
 *   HowItWorks    your machine → policy-gated agent → venue  (the "how")
 *   FAQ           objections + long-tail answers             (depth + SEO)
 *   FinalCTA      install the skill                          (the close)
 *
 * Each content section carries a sequential eyebrow (01–04) so the page
 * reads in order. The deeper product surface (dashboard, marketplace,
 * dev docs) lives in the docs (docs.axonterminal.ai) and the skill repo.
 */
/**
 * Reveal each section (below the hero) as it scrolls into view. JS both
 * arms the hidden state and reveals it, so no-JS / reduced-motion users
 * always see fully-rendered content — nothing can get stranded hidden.
 */
function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    // `.ax-js` is set pre-paint by the head script (and skipped under
    // reduced motion). If it's absent, sections are visible by default
    // and there's nothing to reveal.
    if (!document.documentElement.classList.contains("ax-js")) return;
    // Skip the hero (first section) — it has its own on-load entrance.
    const sections = Array.from(document.querySelectorAll<HTMLElement>("main > section")).slice(1);
    // Safety: if IntersectionObserver is unavailable, reveal everything
    // immediately so armed sections can never stay stranded hidden.
    if (typeof IntersectionObserver === "undefined") {
      sections.forEach((s) => s.classList.add("ax-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("ax-in");
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.04 },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);
}

export function App() {
  useScrollReveal();
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <LogoMarquee />
        <Pillars />
        <AgentExamples />
        <HowItWorks />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </>
  );
}
